import { Box, Divider, InputLabel, } from "@mui/material";
import React from "react";
import ImageBox from "../../../../shared/components/ImageBox";
import colors from "../../../../shared/themes/colors";
import { productCondition, unitCode } from "../../../../common/models/CommonModels";
import {
  StockItemSupplierDetails,
  SupplyVariant
} from "../../../shared/models/customerBuyerStockItemsModels";
import { NumericFormat } from "react-number-format";
import { formatWarrantyFullName } from "../../../../shared/models/models";
import ShieldStarIcon from "../../../../shared/assets/icons/ShieldStarIcon";
import TruncateText from "../../../../shared/components/TruncateText";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface SupplyVariantItemProps {
  supplyVariant: SupplyVariant;
}

const SupplyVariantItem = ({
  supplyVariant,
}: SupplyVariantItemProps) => {
  const { translate: tCommon } = useTranslation("common");
  return (
    <Box
      className="grid grid-cols-3 gap-10 text-14-bold px-4 pt-2 pb-7"
      color={colors.secondary.gray20}
    >
      <Box>
        <Box>{tCommon(`productCondition.${productCondition(supplyVariant.conditionCode)}`)}</Box>

        {!!supplyVariant.warrantyPeriodMonths && (
          <Box className="flex items-center">
            <ShieldStarIcon />
            <Box className="text-12-regular ml-1">
              {formatWarrantyFullName(supplyVariant.warrantyPeriodMonths, true)}
            </Box>
          </Box>
        )}
      </Box>

      <Box>
        <Box>{supplyVariant.quantity.value + "\u00A0" + tCommon(`measurementUnits.${unitCode(supplyVariant.quantity.unitCode)}`)}</Box>
      </Box>
      <Box>
        <NumericFormat value={supplyVariant.price} displayType="text" thousandSeparator=" " suffix="€" />
      </Box>
    </Box>
  );
};

interface ProductVariantsProps {
  supplierDetails?: StockItemSupplierDetails;
}

const ProductVariants = ({ supplierDetails }: ProductVariantsProps) => {
  const firstVariant = supplierDetails?.supplyVariants?.[0];
  const { translate: tMarketplace } = useTranslation("marketplace");
  const { translate: tCommon } = useTranslation("common");

  return (
    <Box className="h-full flex flex-col justify-between">
      <Box>
        <Box className="flex justify-between w-full">
          <Box>
            <Box>
              <Box className="text-16-bold" color={colors.secondary.gray20}>
                {supplierDetails?.number}
              </Box>
              <Box className="text-14-medium">{supplierDetails?.alternativeNumber}</Box>
            </Box>

            <Box className="mt-4">
              <Box className="text-14-medium" color={colors.secondary.gray20}>
                {supplierDetails?.manufacturerName}
              </Box>
              <Box className="text-14-medium">{supplierDetails?.categoryName}</Box>
            </Box>
          </Box>

          <ImageBox src={firstVariant?.image?.content} />
        </Box>

        <Box className="mt-4">
          <InputLabel>{tCommon("description")}</InputLabel>
          <Box
            className="text-14-regular"
            color={colors.secondary.gray20}
            sx={{
              minHeight: "65px"
            }}
          >
            <TruncateText text={firstVariant?.description || ""} maxLength={230} />
          </Box>
        </Box>

        <Box className="mt-6">
          <Box className="text-18-medium" color={colors.secondary.gray20}>
            {tMarketplace("availableProductVariants")}
          </Box>

          <Box
            className="grid grid-cols-3 gap-10 text-12-medium mt-4 px-4 py-2"
            sx={{ borderBottom: `2px solid ${colors.secondary.gray80}` }}
          >
            <Box>{tCommon("condition")}</Box>
            <Box>{tCommon("available")}</Box>
            <Box>{tMarketplace("pricePerPiece")}</Box>
          </Box>

          {supplierDetails?.supplyVariants.map((supplyVariantsItem, i) => (
            <React.Fragment key={i}>
              <SupplyVariantItem
                supplyVariant={supplyVariantsItem}
              />
              {i !== supplierDetails?.supplyVariants.length - 1 && <Divider sx={{ background: colors.secondary.gray80 }} />}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductVariants;